import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  query,
  orderBy 
} from 'firebase/firestore';

/**
 * Service for managing custom styles in Firestore
 * Stores styles under companies/{companyId}/customStyles collection
 */
class CustomStylesService {
  
  /**
   * Get the custom styles collection reference for a company
   */
  getStylesCollection(companyId) {
    return collection(db, 'companies', companyId, 'customStyles');
  }

  /**
   * Get all custom styles for a company
   */
  async getCompanyCustomStyles(companyId) {
    try {
      const stylesRef = this.getStylesCollection(companyId);
      const q = query(stylesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const styles = {};
      snapshot.forEach(doc => {
        styles[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });
      
      return styles;
    } catch (error) {
      console.error('Error fetching custom styles:', error);
      return {};
    }
  }

  /**
   * Create a new custom style
   */
  async createCustomStyle(companyId, styleData) {
    try {
      const stylesRef = this.getStylesCollection(companyId);
      
      const newStyle = {
        name: styleData.name || 'Custom Style',
        colors: styleData.colors,
        font: styleData.font,
        gameType: styleData.gameType || null,
        theme: styleData.theme, // The generated theme object
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(stylesRef, newStyle);
      
      return {
        id: docRef.id,
        ...newStyle
      };
    } catch (error) {
      console.error('Error creating custom style:', error);
      throw error;
    }
  }

  /**
   * Update an existing custom style
   */
  async updateCustomStyle(companyId, styleId, updateData) {
    try {
      const styleRef = doc(db, 'companies', companyId, 'customStyles', styleId);
      
      const updates = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(styleRef, updates);
      
      return {
        id: styleId,
        ...updateData
      };
    } catch (error) {
      console.error('Error updating custom style:', error);
      throw error;
    }
  }

  /**
   * Delete a custom style
   */
  async deleteCustomStyle(companyId, styleId) {
    try {
      const styleRef = doc(db, 'companies', companyId, 'customStyles', styleId);
      await deleteDoc(styleRef);
      return true;
    } catch (error) {
      console.error('Error deleting custom style:', error);
      throw error;
    }
  }

  /**
   * Get a specific custom style
   */
  async getCustomStyle(companyId, styleId) {
    try {
      const styleRef = doc(db, 'companies', companyId, 'customStyles', styleId);
      const docSnap = await getDoc(styleRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching custom style:', error);
      return null;
    }
  }

  /**
   * Duplicate a custom style (create a copy)
   */
  async duplicateCustomStyle(companyId, styleId, newName) {
    try {
      const originalStyle = await this.getCustomStyle(companyId, styleId);
      if (!originalStyle) {
        throw new Error('Original style not found');
      }

      const duplicatedStyle = {
        name: newName || `${originalStyle.name} (Copy)`,
        colors: originalStyle.colors,
        font: originalStyle.font,
        gameType: originalStyle.gameType,
        theme: originalStyle.theme
      };

      return await this.createCustomStyle(companyId, duplicatedStyle);
    } catch (error) {
      console.error('Error duplicating custom style:', error);
      throw error;
    }
  }
}

export default new CustomStylesService(); 